// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ActionableNotification } from '@carbon/react';
import { ArrowLeft } from '@carbon/icons-react';
import {
  useProcedure,
  useProcedureLibrary,
  useSaveProcedure,
  isProcedureValid,
} from '../../shared/procedures';
import { useFlagOn } from '../../shared/featureFlags';
import { useUrlState } from '../../shared/url';
import ProcedureForm from '../components/ProcedureForm';
import ProcedureFormFooter from '../components/ProcedureFormFooter';
import ProcedurePreview from '../components/ProcedurePreview';
import ProcedureJsonPane from '../components/ProcedureJsonPane';
import StatusTag from '../components/StatusTag';
import type { ProcedureDetail } from '../../shared/types/procedures';
import styles from './ProcedureViewPage.module.css';

const LIBRARY_PATH = '/admin-center/procedures/library';

// Behind procedureSubmitEnabled (default off, AB#34512): SOP Factory's
// catalog is read-only — sopFactoryService.updateArtifact has no backend
// route yet, so PUT .../artifacts/{id} always 405s. Disabling the button
// with this message, instead of letting someone fill out the whole form and
// hit "Unable to save this procedure.", is flag-gated like Add procedure /
// Bulk import, but this view stays reachable (it is also the read view), so
// only Submit is disabled rather than the route hidden. Nothing about the save
// flow itself changes, this just stops it from running.
const SUBMIT_DISABLED_REASON = "Submitting changes to a procedure isn't available yet.";

/**
 * View screen for a single procedure — route `procedures/view/:artifactId`.
 * The Guided / raw-JSON switch is `?tab=json` (absent = Guided) so a refresh
 * keeps the pane; any other value falls back to Guided.
 */
export default function ProcedureViewPage() {
  const { artifactId } = useParams<{ artifactId: string }>();
  const navigate = useNavigate();
  // The library links with `?version=` so drafts/deprecated items (which the
  // latest-approved endpoint 404s on) open too; a bare link still resolves to
  // the latest approved version.
  const [searchParams] = useSearchParams();
  const version = searchParams.get('version') || undefined;
  const {
    procedure: loaded,
    artifact,
    isLoading,
    error,
    notFound,
  } = useProcedure(artifactId, version);
  const { domainFacets, equipmentFacets } = useProcedureLibrary({});
  const { save, isSaving, error: saveError } = useSaveProcedure(artifact);
  const procedureSubmitEnabled = useFlagOn('procedureSubmitEnabled');
  const [url, patchUrl] = useUrlState({ tab: 'guided' });
  const tab = url.tab === 'json' ? 1 : 0;
  const setTab = (next: 0 | 1) => patchUrl({ tab: next === 1 ? 'json' : null });
  const [procedure, setProcedure] = useState<ProcedureDetail | null>(null);

  useEffect(() => {
    setProcedure(loaded);
  }, [loaded]);

  async function handleSubmit(nextVersion: string) {
    if (!procedure) return;
    try {
      await save(procedure, nextVersion);
      navigate(LIBRARY_PATH);
    } catch {
      // Surfaced via `saveError` in the footer — stay on the page so
      // nothing typed gets lost.
    }
  }

  return (
    <div className={styles.page}>
      <button type="button" className={styles.backLink} onClick={() => navigate(LIBRARY_PATH)}>
        <ArrowLeft size={16} /> Back to Library
      </button>

      <div className={styles.titleRow}>
        <h1 className={styles.title}>{procedure?.title || 'Procedure'}</h1>
        <div className={styles.switcher} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 0}
            className={`${styles.switchBtn} ${tab === 0 ? styles.switchBtnSelected : ''}`}
            onClick={() => setTab(0)}
          >
            Guided
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 1}
            className={`${styles.switchBtn} ${tab === 1 ? styles.switchBtnSelected : ''}`}
            onClick={() => setTab(1)}
          >
            Advanced: raw JSON
          </button>
        </div>
      </div>

      {procedure && (
        <div className={styles.subtitle}>
          <span>{procedure.artifactType.toUpperCase()}</span>
          <span aria-hidden="true">·</span>
          <StatusTag status={procedure.status} />
        </div>
      )}

      {notFound ? (
        <ActionableNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Procedure not found"
          subtitle={
            version
              ? `No version "${version}" of "${artifactId}" exists in the library. It may have been removed, or the link may be out of date.`
              : `No procedure with ID "${artifactId}" exists in the library. It may have been removed, or the link may be out of date.`
          }
          actionButtonLabel="Return to Library"
          onActionButtonClick={() => navigate(LIBRARY_PATH)}
          className={styles.notification}
        />
      ) : error ? (
        <div className={styles.status}>{error}</div>
      ) : isLoading || !procedure ? (
        <div className={styles.status}>Loading…</div>
      ) : (
        <div className={styles.panes}>
          <div className={styles.pane}>
            <div className={styles.paneScroll}>
              {tab === 0 ? (
                <ProcedureForm
                  procedure={procedure}
                  original={loaded ?? procedure}
                  classificationOptions={domainFacets.map(f => f.value)}
                  equipmentTypeOptions={equipmentFacets.map(f => f.value)}
                  onChange={setProcedure}
                />
              ) : (
                <ProcedureJsonPane procedure={procedure} />
              )}
            </div>
            <ProcedureFormFooter
              version={procedure.version}
              onCancel={() => navigate(LIBRARY_PATH)}
              onSubmit={handleSubmit}
              isSaving={isSaving}
              canSubmit={isProcedureValid(procedure)}
              error={saveError}
              disabledReason={procedureSubmitEnabled ? null : SUBMIT_DISABLED_REASON}
            />
          </div>
          <div className={`${styles.pane} ${styles.panePreview}`}>
            <div className={styles.paneScroll}>
              <ProcedurePreview procedure={procedure} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
